import { useState } from 'react';
import api from '../api/axios';
import { generateTempPassword } from '../utils/tempPassword';
import '../styles/userspage.css';

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());

// Turns the user's saved permissions into a lookup the checkboxes can use:
// { '<companyId>': { PAID: true, UNPAID: false }, ... }
function toAccessMap(permissions = []) {
  const map = {};
  permissions.forEach((p) => {
    const id = p.company?._id || p.company;
    if (!id) return;
    map[id] = { PAID: p.offerTypes.includes('PAID'), UNPAID: p.offerTypes.includes('UNPAID') };
  });
  return map;
}

// Add user (existing = null) or edit user (existing = user object).
// `companies` is the list of active companies to choose from.
export default function UserFormModal({ existing, companies, onClose, onSaved }) {
  const isEdit = Boolean(existing);
  const [name, setName] = useState(existing?.name || '');
  const [email, setEmail] = useState(existing?.email || '');
  const [password, setPassword] = useState(() => (isEdit ? '' : generateTempPassword()));
  const [access, setAccess] = useState(() => toAccessMap(existing?.permissions));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // shows the credentials after creating

  const toggle = (companyId, type) => {
    setAccess((a) => ({
      ...a,
      [companyId]: { ...(a[companyId] || {}), [type]: !a[companyId]?.[type] },
    }));
  };

  // Only active companies are listed, so permissions for a deleted company
  // are dropped automatically the next time the user is saved.
  const buildPermissions = () =>
    companies
      .map((c) => ({
        company: c._id,
        offerTypes: ['PAID', 'UNPAID'].filter((t) => access[c._id]?.[t]),
      }))
      .filter((p) => p.offerTypes.length > 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) return setError('Enter the user\'s name.');
    if (!isEdit && !isValidEmail(email)) return setError('Enter a valid email address.');
    if (!isEdit && password.length < 8) return setError('Temporary password must be at least 8 characters.');

    const permissions = buildPermissions();
    if (permissions.length === 0 && !window.confirm(
      'This user has no company access, so they won\'t be able to generate any offers. Save anyway?'
    )) return;

    setBusy(true);
    try {
      if (isEdit) {
        await api.put(`/users/${existing._id}`, { name, permissions });
        onSaved();
      } else {
        await api.post('/users', { name, email, password, permissions });
        setCreated({ email: email.trim().toLowerCase(), password });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save the user.');
    } finally {
      setBusy(false);
    }
  };

  // After creating: show the credentials once so the admin can share them
  if (created) {
    return (
      <div className="modal-bg">
        <div className="modal">
          <h2>User added</h2>
          <div className="hpu-success">
            Share these sign-in details with {name.trim()}. They'll be asked to set their own password the first time they sign in.
          </div>
          <div className="fg">
            <label>Email</label>
            <div className="hpu-temp-password">{created.email}</div>
          </div>
          <div className="fg">
            <label>Temporary password</label>
            <div className="hpu-temp-password">{created.password}</div>
            <small className="hpu-hint">This password won't be shown again. Copy it now.</small>
          </div>
          <div className="hpu-modal-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigator.clipboard?.writeText(`Email: ${created.email}\nTemporary password: ${created.password}`)}
            >
              Copy details
            </button>
            <button type="button" className="btn btn-primary" onClick={onSaved}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <form className="modal" onSubmit={handleSubmit} noValidate>
        <h2>{isEdit ? `Edit ${existing.name}` : 'Add user'}</h2>
        {error && <div className="login-err">{error}</div>}

        <div className="fg">
          <label>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Priya Sharma" autoFocus />
        </div>

        <div className="fg">
          <label>Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="priya@hirepeaks.com"
            disabled={isEdit}
          />
          {isEdit && <small className="hpu-hint">Email can't be changed. It's the user's sign-in name.</small>}
        </div>

        {!isEdit && (
          <div className="fg">
            <label>Temporary password</label>
            <div className="hpu-password-row">
              <input value={password} onChange={(e) => setPassword(e.target.value)} />
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setPassword(generateTempPassword())}>
                New
              </button>
            </div>
            <small className="hpu-hint">They'll replace it with their own password on first sign-in.</small>
          </div>
        )}

        <div className="fg">
          <label>Company access</label>
          {companies.length === 0 ? (
            <div className="hpu-empty-access">Add a company first, then come back to give access.</div>
          ) : (
            <table className="hpu-access-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th className="hpu-center">Paid</th>
                  <th className="hpu-center">Unpaid</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c._id}>
                    <td>
                      <div className="hpu-company-name">
                        <span className="hpu-swatch" style={{ background: c.accentColor }} />
                        {c.shortName}
                      </div>
                    </td>
                    <td className="hpu-center">
                      <input
                        type="checkbox"
                        aria-label={`${c.shortName} paid offers`}
                        checked={Boolean(access[c._id]?.PAID)}
                        onChange={() => toggle(c._id, 'PAID')}
                      />
                    </td>
                    <td className="hpu-center">
                      <input
                        type="checkbox"
                        aria-label={`${c.shortName} unpaid internship offers`}
                        checked={Boolean(access[c._id]?.UNPAID)}
                        onChange={() => toggle(c._id, 'UNPAID')}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="hpu-modal-actions">
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Add user'}
          </button>
        </div>
      </form>
    </div>
  );
}
